import { useCallback, useContext, useState } from 'react';
import { TimeZoneContext } from '@contexts/timezone-context';
import apiClient from '@utils/api-client';
import { apiInternalGenericEventsPath } from '../../routes';
import { GenericEventType } from '@features/Calendar/constants';
import { RecurrenceFrequency, RecurrenceEndType } from '@features/Modals/NewGenericEventModal/constants';
import useGenericEventProcessor from '@hooks/processors/use-generic-event-processor';
import { dateClient } from '@utils/date-client';
function usePostGenericEvent() {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState();
    const [result, setResult] = useState();
    const { timeZonesMapping } = useContext(TimeZoneContext);
    const { processGenericEvent } = useGenericEventProcessor();
    const save = useCallback((genericEvent) => {
        return new Promise((resolve, reject) => {
            const controller = new AbortController();
            setSaving(true);
            let finalDayOfMonth;
            if (genericEvent.recurrence.frequency === RecurrenceFrequency.Monthly) {
                finalDayOfMonth = genericEvent.recurrence.dayOfMonth;
            }
            else if (genericEvent.recurrence.frequency === RecurrenceFrequency.Annually) {
                finalDayOfMonth = genericEvent.recurrence.annualDate.date();
            }
            else {
                finalDayOfMonth = null;
            }
            const start = dateClient(genericEvent.start).tz(timeZonesMapping[genericEvent.timeZone], true);
            const end = dateClient(genericEvent.end).tz(timeZonesMapping[genericEvent.timeZone], true);
            const recurrenceEnd = dateClient(genericEvent.recurrence.endDate).tz('UTC', true);
            const genericEventInfo = Object.assign({ title: genericEvent.title, description: genericEvent.description, address: genericEvent.eventType === GenericEventType.Site_visit ? genericEvent.address : null, contact_name: genericEvent.eventType === GenericEventType.Site_visit ? genericEvent.contactName : null, contact_phone: genericEvent.eventType === GenericEventType.Site_visit ? genericEvent.contactPhone : null, event_type: genericEvent.eventType, duration: !genericEvent.allDay ? genericEvent.durationMins : null, time_zone_override: genericEvent.timeZone, start_date: genericEvent.allDay ? start.startOf('day').toISOString() : start.toISOString(), end_date: genericEvent.allDay ? end.endOf('day').toISOString() : null, all_day: genericEvent.allDay, conflictable: genericEvent.conflictable, technician_ids: genericEvent.technicianIds, territory_ids: genericEvent.territoryIds }, (genericEvent.isRecurring && {
                recurrence_attributes: {
                    frequency: genericEvent.recurrence.frequency,
                    day_of_week: genericEvent.recurrence.frequency === RecurrenceFrequency.Weekly
                        ? genericEvent.recurrence.dayOfWeek
                        : null,
                    day_of_month: finalDayOfMonth,
                    month_of_year: genericEvent.recurrence.frequency === RecurrenceFrequency.Annually
                        ? genericEvent.recurrence.annualDate.month() + 1
                        : null,
                    recurrence_end_type: genericEvent.recurrence.endType,
                    max_occurrences: genericEvent.recurrence.endType === RecurrenceEndType.After_occurrences
                        ? genericEvent.recurrence.maxOccurrences
                        : null,
                    end_date: genericEvent.recurrence.endType === RecurrenceEndType.On_date
                        ? recurrenceEnd.toISOString()
                        : null,
                    generate_at_once: genericEvent.recurrence.generateAtOnce
                }
            }));
            apiClient
                .post(apiInternalGenericEventsPath(), { generic_event: genericEventInfo }, {
                signal: controller.signal
            })
                .then((response) => {
                const postResult = response.data.data;
                const processedData = postResult.map((event) => processGenericEvent(event));
                setResult(processedData);
                resolve(processedData);
            })
                .catch((err) => {
                if (!controller.signal.aborted) {
                    setResult(undefined);
                    setError(err);
                }
                reject(err);
            })
                .finally(() => {
                if (!controller.signal.aborted)
                    setSaving(false);
            });
        });
    }, [processGenericEvent, timeZonesMapping]);
    return { save, saving, error, result };
}
export default usePostGenericEvent;
